import { Link } from "react-router-dom";
import { useMemo } from "react";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Users, AlertTriangle, Calendar, CheckCircle2, Sparkles, Download, Printer } from "lucide-react";
import AiInsights from "@/components/schools/AiInsights";
import DemoFlow from "@/components/schools/DemoFlow";
import SchoolKpi from "@/components/schools/SchoolKpi";
import SchoolFilters, { useSchoolFilters, termLabel } from "@/components/schools/SchoolFilters";
import { counsellorKpis, counsellorQueue, counsellorCategories, aiInsightsCounsellor } from "@/data/greenwoodPersonas";
import { downloadCsv } from "@/lib/csv";
import { useDynamicInsights } from "@/hooks/useDynamicInsights";
import { cn } from "@/lib/utils";

// District benchmarks — anonymised averages across comparable counsellor caseloads.
const counsellorBenchmarks = { caseload: 380, atRisk: 26, scheduledSessions: 18, interventionsClosed: 34 } as const;

const priorityStyles = {
  high:   "bg-rose-500/10 text-rose-600 ring-rose-500/20",
  medium: "bg-amber-500/10 text-amber-600 ring-amber-500/20",
  low:    "bg-emerald-500/10 text-emerald-600 ring-emerald-500/20",
} as const;

const maxCategory = Math.max(...counsellorCategories.map(c => c.value));

export default function CounsellorDashboard() {
  const [filters] = useSchoolFilters();
  const counsellorInsights = useDynamicInsights(aiInsightsCounsellor, "counsellor");

  const filteredQueue = useMemo(() => {
    return counsellorQueue.filter((r) => {
      if (filters.grade !== "all" && !r.grade.startsWith(filters.grade)) return false;
      if (filters.house !== "all" && r.house !== filters.house) return false;
      return true;
    });
  }, [filters]);

  const exportCaseload = () => {
    const rows: (string | number)[][] = [
      ["WeKIT Education Intelligence™ — Counsellor snapshot"],
      ["Generated", new Date().toISOString()],
      ["Filters", `Grade: ${filters.grade}`, `House: ${filters.house}`, `Term: ${termLabel(filters.term)}`],
      [],
      ["Metric", "Value", "District benchmark"],
      ["Caseload", counsellorKpis.caseload, counsellorBenchmarks.caseload],
      ["At-risk signals", counsellorKpis.atRisk, counsellorBenchmarks.atRisk],
      ["Scheduled sessions", counsellorKpis.scheduledSessions, counsellorBenchmarks.scheduledSessions],
      ["Interventions closed", counsellorKpis.interventionsClosed, counsellorBenchmarks.interventionsClosed],
      [],
      ["Priority queue"],
      ["Student", "Grade", "House", "Signal", "Priority"],
      ...filteredQueue.map((r) => [r.name, r.grade, r.house, r.signal, r.priority]),
      [],
      ["Signal category", "% of caseload"],
      ...counsellorCategories.map((c) => [c.label, c.value]),
    ];
    downloadCsv(`greenwood-counsellor-snapshot-${new Date().toISOString().slice(0, 10)}.csv`, rows);
  };

  return (
    <>
      <PageHead title="Counsellor Dashboard | WeKIT Education Intelligence™" description="Counsellor caseload for WeKIT Education Intelligence™" path="/schools/counsellor" />
      <div className="container max-w-7xl py-8 space-y-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider mb-2">
              <Sparkles className="w-3.5 h-3.5 text-primary" aria-hidden="true" /> Counsellor View · {termLabel(filters.term)}
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Caseload & interventions</h1>
            <p className="text-muted-foreground mt-1">AI-prioritised queue across your students</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={exportCaseload}>
              <Download className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Export CSV
            </Button>
            <Button size="sm" variant="outline" asChild>
              <Link to="/schools/counsellor/onepager"><Printer className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Weekly brief</Link>
            </Button>
          </div>
        </header>

        <SchoolFilters />

        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <SchoolKpi icon={<Users className="w-4 h-4" />}         label="Caseload"              value={counsellorKpis.caseload}                                                       series={[398, 402, 406, 408, 410, 411, 412]} benchmark={counsellorBenchmarks.caseload} />
          <SchoolKpi icon={<AlertTriangle className="w-4 h-4" />} label="At-risk signals"       value={counsellorKpis.atRisk}              hint="Needs review this week" tone="warning"  series={[26, 24, 23, 22, 20, 19, 18]}   benchmark={counsellorBenchmarks.atRisk} />
          <SchoolKpi icon={<Calendar className="w-4 h-4" />}      label="Scheduled sessions"    value={counsellorKpis.scheduledSessions}   hint="Next 7 days"                            series={[16, 18, 19, 21, 22, 23, 24]}   benchmark={counsellorBenchmarks.scheduledSessions} />
          <SchoolKpi icon={<CheckCircle2 className="w-4 h-4" />}  label="Interventions closed"  value={counsellorKpis.interventionsClosed} hint="This term"              tone="positive" series={[22, 27, 32, 36, 40, 44, 47]}   benchmark={counsellorBenchmarks.interventionsClosed} />
        </section>

        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-base">Priority queue</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Showing {filteredQueue.length} of {counsellorQueue.length} students
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              {filteredQueue.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No students match the current filters.
                </div>
              ) : (
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr className="text-left">
                    <th className="p-3">Student</th>
                    <th className="p-3">Grade</th>
                    <th className="p-3">House</th>
                    <th className="p-3">Signal</th>
                    <th className="p-3 text-right">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQueue.map((r) => (
                    <tr key={r.id} className="border-t hover:bg-muted/30 focus-within:bg-muted/30 transition-colors">
                      <td className="p-0">
                        <Link
                          to={`/schools/student?student=${r.id}`}
                          className="block p-3 font-medium text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm"
                          aria-label={`Open ${r.name}'s student profile, Grade ${r.grade}, ${r.priority} priority`}
                        >
                          {r.name}
                        </Link>
                      </td>
                      <td className="p-3 text-muted-foreground">{r.grade}</td>
                      <td className="p-3 text-muted-foreground">{r.house}</td>
                      <td className="p-3">{r.signal}</td>
                      <td className="p-3 text-right">
                        <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ring-1", priorityStyles[r.priority])}>
                          {r.priority}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Signal categories</CardTitle>
              <p className="text-xs text-muted-foreground">What students bring most this term</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {counsellorCategories.map((c) => (
                <div key={c.label}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{c.label}</span>
                    <span className="tabular-nums text-muted-foreground">{c.value}%</span>
                  </div>
                  <Progress value={(c.value / maxCategory) * 100} className="h-1.5 mt-1.5" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <AiInsights insights={counsellorInsights.insights} contextLabel={counsellorInsights.contextLabel} title="AI-guided intervention signals" subtitle="Where a small nudge could change a term" />
        <DemoFlow />
      </div>
    </>
  );
}
